import React from "react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { Spin } from "antd";
import type { ButtonVariant, ButtonSize, ButtonProps } from "../../types";

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    "bg-primary text-white hover:bg-primary-hover focus:ring-primary/20 border-transparent",
  secondary:
    "bg-secondary text-white hover:bg-secondary-hover focus:ring-secondary/20 border-transparent",
  outline:
    "bg-transparent text-primary hover:bg-primary-light border-primary focus:ring-primary/20",
  ghost:
    "bg-transparent text-grey hover:bg-offwhite border-transparent focus:ring-gray-200",
  danger:
    "bg-red-600 text-white hover:bg-red-700 focus:ring-red-200 border-transparent",
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: "px-3 h-[24px] text-xs gap-1.5",
  md: "px-4 h-[28px] text-sm gap-2",
  lg: "px-6 h-[32px] text-sm gap-2.5",
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      loading = false,
      icon,
      iconPosition = "left",
      fullWidth = false,
      children,
      className,
      disabled,
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        className={twMerge(
          clsx(
            "inline-flex items-center justify-center font-semibold rounded border",
            "duration-200 focus:outline-none focus:ring-2",
            variantStyles[variant],
            sizeStyles[size],
            fullWidth && "w-full",
            isDisabled && "opacity-50 cursor-not-allowed",
            className,
          ),
        )}
        disabled={isDisabled}
        {...props}
      >
        {loading ? (
          <Spin size="small" className="mr-2" />
        ) : icon && iconPosition === "left" ? (
          <span className="flex-shrink-0">{icon}</span>
        ) : null}
        {children}
        {!loading && icon && iconPosition === "right" && (
          <span className="flex-shrink-0">{icon}</span>
        )}
      </button>
    );
  },
);

Button.displayName = "Button";

export default Button;
